import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import LoadingWrapper from 'components/LoadingWrapper';
import { filterOptionLowerCaseInclude } from 'pages/appSettings/allocateSettings/ViewerPage';
import {
  PrefReviewLoading,
  PrefReviewSelector,
  type TViewLinkSubmitter,
  getViewLink,
  updatePrefReview,
} from 'slices/appsPrefReviewSettings';
import { fetchFields } from 'slices/field.slice';
import { organizationSelector } from 'slices/organization.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import {
  requestViewerPages,
  viewerPagesSelector,
} from 'slices/viewerPages.slice';
import { Button, DefaultContentLayout } from '@timeedit/ui-components';
import {
  Alert,
  Col,
  Input,
  Radio,
  Row,
  Select,
} from '@timeedit/ui-components/antd';
import intl from '../../../../i18n/intl';
import { SHOW_REVIEW_LINK_OPTIONS } from './options.constants';
import './ReviewSettings.scss';

const language: any = intl.messages;

type TFormData = {
  viewLinkSubmitter?: TViewLinkSubmitter;
  viewerEntry?: string;
  viewerPath?: string;
  viewerPage?: string;
  commentField?: string;
};

const ReviewSettings = () => {
  const dispatch = useAppDispatch();
  const organizationDetails = useSelector(PrefReviewSelector);
  const organization = useAppSelector(organizationSelector);
  const isLoading = useSelector(PrefReviewLoading);

  const pages = useSelector(viewerPagesSelector);

  useEffect(() => {
    dispatch(getViewLink());
    dispatch(requestViewerPages(organization?.customerSignature));
    dispatch(fetchFields());
  }, [dispatch]);

  const [form, setForm] = useState<TFormData>({
    // TODO: We need validation for this field since the BE expects a boolean one of the typed values
    viewLinkSubmitter: '' as TViewLinkSubmitter,
    viewerEntry: '',
    viewerPath: '',
    viewerPage: '',
    commentField: '',
  });

  const _handleSubmit = () => {
    dispatch(updatePrefReview({ ...form }));
  };

  useEffect(() => {
    setForm(organizationDetails);
  }, [organizationDetails]);

  return (
    <DefaultContentLayout
      title={language.preferences_settings}
      subtitle={language.review_settings_let_you_configure}
      actions={
        <Button type="primary" onClick={_handleSubmit}>
          {language.save_changes as string}
        </Button>
      }
    >
      <LoadingWrapper isLoading={isLoading}>
        <div
          className="review-settings"
          style={{ maxWidth: '80rem', padding: 15 }}
        >
          <div>
            <div>
              <h3>{'Link to Viewer'}</h3>
              <Row align="middle" style={{ paddingBottom: 8 }}>
                <Col>
                  <Select
                    className="page-dropdown"
                    showSearch
                    placeholder="Select a page to populate the fields below"
                    onChange={(value, option) => {
                      if (!option) {
                        return;
                      }
                      setForm({
                        ...form,
                        viewerEntry: `${option['urlstart']}`,
                      });
                    }}
                    filterOption={filterOptionLowerCaseInclude}
                    options={pages}
                    popupMatchSelectWidth={false}
                  />
                </Col>
              </Row>
              <Row align="middle" style={{ paddingBottom: 8 }}>
                <Col span={1}>{'Entry'}&nbsp; </Col>
                <Col style={{ flex: 1 }}>
                  <Input
                    value={form.viewerEntry}
                    onChange={(e: any) =>
                      setForm({ ...form, viewerEntry: e.target.value })
                    }
                  />
                </Col>
              </Row>
              <Alert
                description={language.if_this_settings}
                type="info"
                showIcon={false}
              />
            </div>
          </div>
          <br />
          <br />
          <div>
            <h3>{language.display_and_notification_settings}</h3>
            <h4 className="text--secondary">{language.this_controls_when}</h4>
            <div>
              {language.Show_link_when}:
              <Radio.Group
                value={form.viewLinkSubmitter}
                onChange={(e) =>
                  setForm({
                    ...form,
                    viewLinkSubmitter: e.target.value,
                  })
                }
              >
                {Object.values(SHOW_REVIEW_LINK_OPTIONS).map(
                  ({ value, label }) => (
                    <Radio key={value} value={value}>
                      {label}
                    </Radio>
                  )
                )}
              </Radio.Group>
            </div>
          </div>
        </div>
      </LoadingWrapper>
    </DefaultContentLayout>
  );
};

export default ReviewSettings;
